import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { capabilities, industries } from "../data/mock";
import {
  FileCode2,
  Boxes,
  ShieldCheck,
  Workflow,
  ArrowRight,
  Check,
  Dices,
  BatteryCharging,
  Radar,
} from "lucide-react";
import { TechStackGrid } from "../components/brand/TechLogos";
import { Reveal } from "../components/animations/Reveal";
import { cn } from "@/lib/utils";

const iconMap = { FileCode2, Boxes, ShieldCheck, Workflow };
const industryIcons = { Dices, BatteryCharging, Radar };

export default function Services() {
  return (
    <div className="bg-background">
      {/* Hero Section */}
      <section className="relative overflow-hidden py-20 lg:py-28 border-b border-border">
        <div className="absolute inset-0 graph-bg" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-primary text-sm font-mono uppercase tracking-wider mb-4">Core Capabilities</p>
            <h1 className="text-4xl lg:text-5xl xl:text-6xl font-bold text-foreground leading-tight mb-6">
              Infrastructure, Orchestration, Security, Automation
            </h1>
            <p className="text-lg lg:text-xl text-muted-foreground leading-relaxed">
              Four disciplines, applied to your industry's specific data — not a generic cloud
              consulting checklist.
            </p>
          </div>
        </div>
      </section>

      {/* Capabilities List */}
      <section className="py-12 lg:py-20 border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="space-y-16 lg:space-y-24">
            {capabilities.map((cap, index) => {
              const Icon = iconMap[cap.icon] || FileCode2;
              const isEven = index % 2 === 0;
              const relatedIndustries = industries.filter((ind) =>
                ind.relatedCapabilities.includes(cap.slug)
              );

              return (
                <div key={cap.id} id={cap.slug} className="scroll-mt-24">
                  <div className={`flex flex-col ${isEven ? "lg:flex-row" : "lg:flex-row-reverse"} gap-12 lg:gap-16 items-start`}>
                    {/* Content */}
                    <div className="flex-1">
                      <div className="flex items-center gap-4 mb-6">
                        <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
                          <Icon className="w-7 h-7 text-primary" />
                        </div>
                      </div>
                      <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
                        {cap.title}
                      </h2>
                      <p className="text-muted-foreground text-lg leading-relaxed mb-6">
                        {cap.description}
                      </p>

                      {relatedIndustries.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-8">
                          {relatedIndustries.map((ind) => {
                            const IndIcon = industryIcons[ind.icon] || Dices;
                            return (
                              <Link
                                key={ind.slug}
                                to={`/industries/${ind.slug}`}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-secondary border border-border text-xs text-secondary-foreground hover:border-primary/40 transition-colors"
                              >
                                <IndIcon className="w-3.5 h-3.5" />
                                {ind.name}
                              </Link>
                            );
                          })}
                        </div>
                      )}

                      <Link to="/contact">
                        <Button className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">
                          Get Started
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </Link>
                    </div>

                    {/* Features Card */}
                    <Card className="flex-1 bg-card border-border">
                      <CardHeader>
                        <CardTitle className="text-foreground flex items-center gap-2">
                          <Icon className="w-5 h-5 text-primary" />
                          What's Included
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ul className="space-y-4">
                          {cap.features.map((feature, i) => (
                            <li key={i} className="flex items-start gap-3">
                              <div className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/15 flex items-center justify-center mt-0.5">
                                <Check className="w-3 h-3 text-primary" />
                              </div>
                              <span className="text-secondary-foreground">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  </div>

                  {index < capabilities.length - 1 && (
                    <div className="mt-16 lg:mt-24 border-t border-border" />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Tech Stack Section */}
      <section className="py-20 lg:py-28 bg-card/40 border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal className="text-center mb-12">
            <h2 className="text-2xl lg:text-3xl font-bold text-foreground mb-4">
              Platform Expertise
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Deep expertise across major cloud, container, and automation platforms
            </p>
          </Reveal>
          <TechStackGrid />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal className="relative overflow-hidden rounded-3xl bg-card border border-border p-12 lg:p-16 text-center">
            <div className="absolute inset-0 graph-bg-dense opacity-50" />
            <div className="relative">
              <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
                Not Sure Which Capability You Need?
              </h2>
              <p className="text-muted-foreground text-lg max-w-2xl mx-auto mb-8">
                Tell us about your infrastructure and data, and we'll scope the right combination
                of IaC, orchestration, security, and automation.
              </p>
              <Link to="/contact">
                <Button
                  size="lg"
                  className={cn("bg-primary text-primary-foreground hover:bg-primary/90 font-semibold px-8 h-12")}
                >
                  Talk to an Architect
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
